import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { ICertificateEmployeeDTO } from '@sharedInterfaces/ICertificate';
import type { ISmallCertificate } from '@sharedInterfaces/IWhoIAm';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import changeMapCertificate from '@src/APIs/graphQl/Certificate/changeMapCertificate';
import getCertificates from '@src/APIs/graphQl/Certificate/getCertificates';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { useMapCertificateDialog } from '@src/App/Components/Dialogs/MapCertificateDialog/MapCertificateDialog';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import { setEmployeeCertificates, setFeaturedCertificateId } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import { Award, Badge, Building, Eye, Plus, Search, Trash2 } from 'lucide-react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ProfileTabHeader } from './ProfileTabHeader';

export function CertificatesTab(): JSX.Element {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const showErrorDialog = useErrorDialog();
    const mapCertificateDialog = useMapCertificateDialog();

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).profileSettings;
    const allCertificates = useSelector((state: AppState) => state.company.allCertificates);
    const employee = useSelector((state: AppState) => state.employee);
    const offline = useSelector((state: AppState) => state.client.offline);
    const isAdmin = useSelector((state: AppState) => state.permissions.admin);

    const featuredCertificateId = useSelector((state: AppState) => state.employee.featuredCertificateId);
    const certificates = [...employee.certificates].sort((a, b) => a.title.localeCompare(b.title));

    const [selectedCertificate, setSelectedCertificate] = React.useState<ISmallCertificate | null>(null);
    const [newCertificateName, setNewCertificateName] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [deletingIds, setDeletingIds] = React.useState<number[]>([]);
    const [addingCerts, setAddingCerts] = React.useState<string[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [page, setPage] = React.useState(1);
    const [sortField, setSortField] = React.useState<'title' | 'manufacturer' | 'status'>('title');
    const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

    const ITEMS_PER_PAGE = 12;

    // Load certificates on mount
    React.useEffect(() => {
        if (allCertificates.length === 0) {
            getCertificates().catch(console.error);
        }
    }, [allCertificates.length]);

    // Initialize featured certificate to first certificate if not set
    React.useEffect(() => {
        if (certificates.length > 0 && featuredCertificateId === null) {
            console.log('🚀 Auto-setting first certificate as featured:', certificates[0].id);
            dispatch(setFeaturedCertificateId(certificates[0].id || null));
        }
    }, [certificates, featuredCertificateId, dispatch]);

    const handleAddExisting = async () => {
        if (!selectedCertificate) {
            setError('Bitte wählen Sie ein Zertifikat aus');
            return;
        }

        setError(null);
        const certTitle = selectedCertificate.title;
        setAddingCerts((prev) => [...prev, certTitle]);
        setSelectedCertificate(null);

        try {
            const result = await mapCertificateDialog(selectedCertificate.id, undefined, 'full');
            if (result.success) {
                // Certificate was successfully added via dialog
                console.log('✅ Certificate added successfully:', selectedCertificate.id);
            }
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Hinzufügen des Zertifikats');
        } finally {
            setAddingCerts((prev) => prev.filter((t) => t !== certTitle));
        }
    };

    const handleCreateNew = async () => {
        if (!newCertificateName.trim()) {
            setError('Bitte geben Sie einen Namen ein');
            return;
        }

        setError(null);
        const certificateName = newCertificateName;
        setAddingCerts((prev) => [...prev, certificateName]);
        setNewCertificateName('');

        try {
            const result = await mapCertificateDialog(undefined, certificateName, 'full');
            if (result.success) {
                // Certificate was successfully created and added via dialog
                console.log('✅ New certificate created successfully:', certificateName);
            }
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Erstellen des Zertifikats');
        } finally {
            setAddingCerts((prev) => prev.filter((t) => t !== certificateName));
        }
    };

    const handleRemove = async (certificate: { title: string; id?: number }) => {
        if (!certificate.id) return;

        setDeletingIds((prev) => [...prev, certificate.id as number]);

        try {
            await changeMapCertificate(certificate.id, false, undefined, undefined);
            dispatch(setEmployeeCertificates(employee.certificates.filter((c) => c.id !== certificate.id)));
            console.log('✅ Certificate removed successfully:', certificate.id);
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Entfernen des Zertifikats');
        } finally {
            setDeletingIds((prev) => prev.filter((d) => d !== certificate.id));
        }
    };

    const handleSetFeatured = (certificateId: number | undefined) => {
        if (certificateId) {
            console.log('🔄 Setting Featured Certificate ID:', certificateId);
            dispatch(setFeaturedCertificateId(certificateId));
            console.log('✅ Dispatched setFeaturedCertificateId');
            /* TODO: Backend Implementation for Featured Certificate Feature
             *
             * REQUIRED CHANGES:
             *
             * 1. DATABASE SCHEMA:
             *    - Add 'isFeatured' boolean field to employee_certificates table
             *    - Ensure only one certificate per employee can be featured
             *
             * 2. INTERFACE UPDATES:
             *    - Update ICertificateEmployeeDTO in ICertificate.ts:
             *      interface ICertificateEmployeeDTO {
             *        isFeatured?: boolean;
             *      }
             *
             * 3. API IMPLEMENTATION:
             *    - Create setFeaturedCertificate function
             *    - Update Redux state after successful API call
             *
             * 4. PROFILE HEADER UPDATE:
             *    - Show featured certificate badge/logo in profile header
             *    - Display certificate name with special styling
             *
             * IMPACT: This feature enables users to showcase their most important
             * certificate in their public profile, improving professional representation.
             */
        }
    };
    const getStatus = React.useCallback((cert: ICertificateEmployeeDTO) => {
        const now = new Date();
        if (cert.endDate?.getTime) {
            const daysDiff = (cert.endDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);
            if (daysDiff <= 0) return 'Abgelaufen';
            if (daysDiff <= 60) return 'Läuft ab';
        }
        return 'Aktiv';
    }, []);

    // Filter and sort certificates
    const filteredAndSortedCertificates = React.useMemo(() => {
        const filtered = certificates.filter((cert) => cert.title.toLowerCase().includes(searchTerm.toLowerCase()));

        return filtered.sort((a, b) => {
            let aValue: string;
            let bValue: string;

            if (sortField === 'title') {
                aValue = a.title;
                bValue = b.title;
            } else if (sortField === 'manufacturer') {
                // TODO: ISmallCertificate has no productInfo field
                // Options: 1) Load full ICertificateDTO for each cert, or 2) Add productInfo to ISmallCertificate
                aValue = 'Unbekannt';
                bValue = 'Unbekannt';
            } else {
                // status
                aValue = getStatus(a);
                bValue = getStatus(b);
            }

            const comparison = aValue.localeCompare(bValue);
            return sortDirection === 'desc' ? -comparison : comparison;
        });
    }, [certificates, searchTerm, sortField, sortDirection, getStatus]);
    if (!employee) return <LoadingBox />;

    // Pagination
    const totalPages = Math.ceil(filteredAndSortedCertificates.length / ITEMS_PER_PAGE);
    const paginatedCertificates = filteredAndSortedCertificates.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE,
    );

    const availableCertificates = allCertificates.filter(
        (cert) => !certificates.find((existing) => existing.id === cert.id || existing.title === cert.title),
    );

    const handleSort = (field: 'title' | 'manufacturer' | 'status') => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setPage(1);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ProfileTabHeader
                title={langStrings.yourCertificates}
                subtitle="Verwalte deine Zertifikate und Qualifikationen"
            />

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ px: { xs: 1, md: 3, lg: 4 }, py: { xs: 1, md: 3, lg: 4 }, pt: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Zertifikat hinzufügen
                    </Typography>

                    {/* Bestehendes Zertifikat hinzufügen */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 2,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            bgcolor: alpha(theme.palette.info.main, 0.02),
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Search size={16} color={theme.palette.info.main} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                                Bestehendes Zertifikat hinzufügen
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Durchsucht verfügbare Zertifikate in deinem Unternehmen
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Autocomplete
                                value={selectedCertificate}
                                onChange={(_, newValue) => setSelectedCertificate(newValue)}
                                options={availableCertificates}
                                getOptionLabel={(option) => option.title}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Zertifikat auswählen..."
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        <Award size={16} style={{ opacity: 0.6 }} />
                                        <Typography>{option.title}</Typography>
                                    </Box>
                                )}
                                sx={{ flex: 1 }}
                                disabled={offline}
                            />
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleAddExisting}
                                disabled={
                                    !selectedCertificate ||
                                    offline ||
                                    (selectedCertificate && addingCerts.includes(selectedCertificate.title))
                                }
                                startIcon={<Plus size={16} />}
                            >
                                {selectedCertificate && addingCerts.includes(selectedCertificate.title)
                                    ? 'Wird hinzugefügt...'
                                    : 'Hinzufügen'}
                            </Button>
                        </Box>
                    </Paper>

                    {/* Neues Zertifikat erstellen - nur für Admins */}
                    {isAdmin && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                bgcolor: alpha(theme.palette.success.main, 0.02),
                                borderRadius: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Plus size={16} color={theme.palette.success.main} />
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: theme.palette.success.main }}
                                >
                                    Neues Zertifikat erstellen
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Erstellt ein komplett neues Zertifikat für dein Unternehmen
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    value={newCertificateName}
                                    onChange={(e) => setNewCertificateName(e.target.value)}
                                    placeholder="Name des neuen Zertifikats..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ flex: 1 }}
                                    disabled={offline}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleCreateNew();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleCreateNew}
                                    disabled={
                                        !newCertificateName.trim() ||
                                        offline ||
                                        addingCerts.includes(newCertificateName)
                                    }
                                    startIcon={<Plus size={16} />}
                                >
                                    {addingCerts.includes(newCertificateName) ? 'Wird erstellt...' : 'Erstellen'}
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, mt: 0 }}>
                        {error}
                    </Alert>
                )}

                {/* Suchfeld für bestehende Zertifikate */}
                <Box sx={{ px: { xs: 1, md: 3, lg: 4 }, py: { xs: 1, md: 3, lg: 4 }, pt: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Meine Zertifikate ({filteredAndSortedCertificates.length})
                    </Typography>
                    <TextField
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        placeholder="In deinen Zertifikaten suchen..."
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: <Search size={20} style={{ marginRight: 8, opacity: 0.6 }} />,
                        }}
                        sx={{ maxWidth: 400, mt: 1 }}
                    />
                </Box>

                <TableContainer sx={{ px: { xs: 1, md: 3, lg: 4 }, pb: { xs: 1, md: 3, lg: 4 } }}>
                    <Table sx={{ '& .MuiTableCell-root': { px: { xs: 1, md: 3, lg: 4 } } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                        active={sortField === 'title'}
                                        direction={sortField === 'title' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('title')}
                                    >
                                        Zertifikat
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 140 }}>
                                    <TableSortLabel
                                        active={sortField === 'manufacturer'}
                                        direction={sortField === 'manufacturer' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('manufacturer')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Building size={14} />
                                            Anbieter
                                        </Box>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 120 }}>
                                    <TableSortLabel
                                        active={sortField === 'status'}
                                        direction={sortField === 'status' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('status')}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 100 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Badge size={14} />
                                        Anzeigen
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, width: 120 }}>
                                    Aktionen
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedCertificates.map((certificate) => {
                                // TODO: ISmallCertificate has no productInfo field
                                // Options: 1) Load full ICertificateDTO, or 2) Add productInfo to ISmallCertificate
                                const manufacturer = 'Unbekannt';

                                // Status calculation
                                const now = new Date();
                                let status = 'Aktiv';
                                let statusColor = theme.palette.success.main;

                                if (certificate.endDate?.getTime) {
                                    const daysDifference =
                                        (certificate.endDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);

                                    if (daysDifference <= 0) {
                                        status = 'Abgelaufen';
                                        statusColor = theme.palette.grey[600];
                                    } else if (daysDifference <= 60) {
                                        status = 'Läuft ab';
                                        statusColor = theme.palette.warning.main;
                                    }
                                }

                                return (
                                    <TableRow
                                        key={certificate.id || certificate.title}
                                        sx={{
                                            borderLeft: '2px solid transparent',
                                            '&:hover': {
                                                backgroundColor: `${alpha(theme.palette.primary.main, 0.02)} !important`,
                                                borderLeft: `2px solid ${theme.palette.primary.main}`,
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Award size={16} style={{ opacity: 0.6 }} />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {certificate.title}
                                                    </Typography>
                                                    {!certificate.id && (
                                                        <Chip
                                                            label="Neu"
                                                            size="small"
                                                            color="primary"
                                                            sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={manufacturer}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    bgcolor:
                                                        manufacturer === 'Unbekannt'
                                                            ? alpha(theme.palette.grey[500], 0.05)
                                                            : alpha(theme.palette.info.main, 0.05),
                                                    borderColor:
                                                        manufacturer === 'Unbekannt'
                                                            ? alpha(theme.palette.grey[500], 0.2)
                                                            : alpha(theme.palette.info.main, 0.2),
                                                    color:
                                                        manufacturer === 'Unbekannt'
                                                            ? theme.palette.grey[600]
                                                            : theme.palette.info.main,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={status}
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                    bgcolor: alpha(statusColor, 0.1),
                                                    color: statusColor,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleSetFeatured(certificate.id)}
                                                disabled={offline}
                                                title={
                                                    featuredCertificateId === certificate.id
                                                        ? 'Nicht im Profil anzeigen'
                                                        : 'Im Profil anzeigen'
                                                }
                                                sx={{
                                                    color:
                                                        featuredCertificateId === certificate.id
                                                            ? theme.palette.success.main
                                                            : theme.palette.action.disabled,
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                                    },
                                                }}
                                            >
                                                <Badge
                                                    size={16}
                                                    fill={
                                                        featuredCertificateId === certificate.id
                                                            ? 'currentColor'
                                                            : 'none'
                                                    }
                                                />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                {certificate.id && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/certificates/${certificate.id}`)}
                                                        title="Zertifikat anzeigen"
                                                        sx={{
                                                            color: theme.palette.info.main,
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                                            },
                                                        }}
                                                    >
                                                        <Eye size={16} />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemove(certificate)}
                                                    disabled={deletingIds.includes(certificate.id as number) || offline}
                                                    title="Zertifikat entfernen"
                                                    color="primary"
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Empty State */}
                {/* Empty State */}
                {filteredAndSortedCertificates.length === 0 && certificates.length > 0 && (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Award size={48} color={theme.palette.text.disabled} style={{ marginBottom: 16 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            Keine Zertifikate gefunden
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Keine Zertifikate gefunden für &quot;{searchTerm}&quot;
                        </Typography>
                    </Box>
                )}

                {certificates.length === 0 && (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Award size={48} color={theme.palette.text.disabled} style={{ marginBottom: 16 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            Noch keine Zertifikate
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Fügen Sie Ihr erstes Zertifikat hinzu, um Ihre Qualifikationen zu verwalten
                        </Typography>
                    </Box>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
